import type { QueryFunction } from '@tanstack/svelte-query'

import { handleFetch } from '@utils/fetch'

interface ExternalEvalTask {
  id: string
  name: string
  sampleCount: number | null
}

interface ExternalEvalBenchmark {
  id: string
  title: string
  description: string
  category: string
  taskVersion: string
  paperUrl: string | null
  tasks: ExternalEvalTask[]
}

interface ExternalEvalsCatalog {
  schemaVersion: 1
  source: {
    inspectAi: string
    inspectEvals: string
  }
  benchmarks: ExternalEvalBenchmark[]
}

const DEFAULT_INSPECT_EVAL_TASK_IDS = [
  'inspect_evals/aime2025',
  'inspect_evals/gpqa_diamond',
  'inspect_evals/mmlu_pro',
]
const DEFAULT_INSPECT_EVAL_SAMPLE_LIMIT = 250
const DEFAULT_INSPECT_EVAL_MAX_MODEL_LEN = 32768
const externalEvalsCatalogQueryKey = ['external-evals-catalog'] as const

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const requireString = (value: unknown, field: string) => {
  if (typeof value !== 'string') {
    throw new Error(`Invalid external evals catalog: ${field} must be a string.`)
  }
  return value
}

const requireNullableString = (value: unknown, field: string) => {
  if (value !== null && typeof value !== 'string') {
    throw new Error(`Invalid external evals catalog: ${field} must be a string or null.`)
  }
  return value
}

const requireNullableNumber = (value: unknown, field: string) => {
  if (value !== null && typeof value !== 'number') {
    throw new Error(`Invalid external evals catalog: ${field} must be a number or null.`)
  }
  return value
}

const parseTask = (value: unknown, benchmarkIndex: number, taskIndex: number): ExternalEvalTask => {
  const fieldPrefix = `benchmarks[${benchmarkIndex}].tasks[${taskIndex}]`
  if (!isRecord(value)) {
    throw new Error(`Invalid external evals catalog: ${fieldPrefix} must be an object.`)
  }
  return {
    id: requireString(value.id, `${fieldPrefix}.id`),
    name: requireString(value.name, `${fieldPrefix}.name`),
    sampleCount: requireNullableNumber(value.sample_count, `${fieldPrefix}.sample_count`),
  }
}

const parseBenchmark = (value: unknown, index: number): ExternalEvalBenchmark => {
  const fieldPrefix = `benchmarks[${index}]`
  if (!isRecord(value)) {
    throw new Error(`Invalid external evals catalog: ${fieldPrefix} must be an object.`)
  }
  if (!Array.isArray(value.tasks) || value.tasks.length === 0) {
    throw new Error(`Invalid external evals catalog: ${fieldPrefix}.tasks must be non-empty.`)
  }
  return {
    id: requireString(value.id, `${fieldPrefix}.id`),
    title: requireString(value.title, `${fieldPrefix}.title`),
    description: requireString(value.description, `${fieldPrefix}.description`),
    category: requireString(value.category, `${fieldPrefix}.category`),
    taskVersion: requireString(value.task_version, `${fieldPrefix}.task_version`),
    paperUrl: requireNullableString(value.paper_url, `${fieldPrefix}.paper_url`),
    tasks: value.tasks.map((task, taskIndex) => parseTask(task, index, taskIndex)),
  }
}

const parseExternalEvalsCatalog = (value: unknown): ExternalEvalsCatalog => {
  if (!isRecord(value) || value.schema_version !== 1 || !isRecord(value.source)) {
    throw new Error('Invalid external evals catalog response.')
  }
  if (!Array.isArray(value.benchmarks)) {
    throw new Error('Invalid external evals catalog: benchmarks must be an array.')
  }
  return {
    schemaVersion: 1,
    source: {
      inspectAi: requireString(value.source.inspect_ai, 'source.inspect_ai'),
      inspectEvals: requireString(value.source.inspect_evals, 'source.inspect_evals'),
    },
    benchmarks: value.benchmarks.map(parseBenchmark),
  }
}

const fetchExternalEvalsCatalog: QueryFunction<ExternalEvalsCatalog> = async ({ signal }) => {
  const response = await handleFetch('/external-evals/catalog', {
    method: 'GET',
    signal,
  })
  return parseExternalEvalsCatalog(await response.json())
}

const externalEvalsCatalogQuery = () => ({
  queryKey: externalEvalsCatalogQueryKey,
  queryFn: fetchExternalEvalsCatalog,
})

export {
  DEFAULT_INSPECT_EVAL_MAX_MODEL_LEN,
  DEFAULT_INSPECT_EVAL_SAMPLE_LIMIT,
  DEFAULT_INSPECT_EVAL_TASK_IDS,
  type ExternalEvalBenchmark,
  type ExternalEvalsCatalog,
  externalEvalsCatalogQuery,
  externalEvalsCatalogQueryKey,
  type ExternalEvalTask,
  parseExternalEvalsCatalog,
}
